import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { Button, Form } from 'react-bootstrap';
import { ArrowsFullscreen, Eye, EyeSlash, FullscreenExit } from 'react-bootstrap-icons';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import addDays from 'date-fns/addDays';
import getDay from 'date-fns/getDay';
import isWithinInterval from 'date-fns/isWithinInterval';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './styles/colors.css';
import './AppointmentsCalendar.css';
import { effectiveAppointmentType, listRowKey } from '../utils/effectiveAppointmentType';
import { patientSurgeryDate } from '../utils/patientSurgeryDate';
import {
  getPatientPrivacyLabel,
  PrivatePatientName,
} from '../utils/patientNamePrivacy';
import CalendarAnalyticsDashboard from './CalendarAnalyticsDashboard';

const locales = { 'en-US': require('date-fns/locale/en-US') };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Theme colors for appointment types
const MED_MANAGEMENT_COLOR = '#F0C6C6';  // --mm-primary
const JOINT_DECISIONS_COLOR = '#C6DAF0';  // --jd-primary

function getRangeForView(date, view, excludeWeekends) {
  const d = new Date(date);
  if (view === 'day') {
    return { start: startOfDay(d), end: endOfDay(d) };
  }
  if (view === 'month') {
    return { start: startOfMonth(d), end: endOfMonth(d) };
  }
  if (view === 'work_week') {
    const mon = startOfWeek(d, { weekStartsOn: 1 });
    const fri = endOfDay(addDays(mon, 4));
    return { start: mon, end: fri };
  }
  // week (Sun-Sat)
  return { start: startOfWeek(d, { weekStartsOn: 0 }), end: endOfWeek(d, { weekStartsOn: 0 }) };
}

function getRangeLabel(view) {
  if (view === 'day') return 'this day';
  if (view === 'month') return 'this month';
  return 'this week';
}

function formatAppointmentTime(date) {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const CALENDAR_MIN_TIME = new Date(1970, 0, 1, 7, 0, 0);
const CALENDAR_MAX_TIME = new Date(1970, 0, 1, 20, 0, 0);
const CALENDAR_STYLE = { height: '100%' };
const WEEKDAY_VIEWS = ['work_week', 'day', 'month'];
const WEEKEND_VIEWS = ['week', 'day', 'month'];

function sameCalendarDay(a, b) {
  return Boolean(a && b) && startOfDay(a).getTime() === startOfDay(b).getTime();
}

function eventStyleGetter(event) {
  const bgColor = event.isMedManagement ? MED_MANAGEMENT_COLOR : JOINT_DECISIONS_COLOR;
  const borderColor = event.isMedManagement ? '#8F2020' : '#205c8f';
  return {
    style: {
      backgroundColor: bgColor,
      color: borderColor,
      borderRadius: '4px',
      border: `1px solid ${borderColor}`,
    },
  };
}

function AppointmentEvent({ event, title, maskPatientNames }) {
  const fullName = event.patientName || event.title || title;
  return (
    <div className="appointment-calendar-event">
      <div
        className="appointment-calendar-event__name"
        aria-label={maskPatientNames ? 'Patient appointment' : fullName}
      >
        {maskPatientNames ? (
          <PrivatePatientName name={fullName} />
        ) : (
          fullName
        )}
      </div>
      <div className="appointment-calendar-event__time">{formatAppointmentTime(event.start)}</div>
    </div>
  );
}

const CALENDAR_FORMATS = {
  eventTimeRangeFormat: () => null,
};

function CalendarLegend({ showDescriptions = true }) {
  return (
    <div className="calendar-legend">
      <div className="calendar-legend__items">
        <div className="calendar-legend__item">
          <span
            className="calendar-legend__swatch"
            style={{ backgroundColor: JOINT_DECISIONS_COLOR, borderColor: '#205c8f' }}
            aria-hidden
          />
          <small className="fw-semibold calendar-legend__label">Joint Decisions</small>
          {showDescriptions && (
            <small className="text-muted calendar-legend__description">
              Risk profiles for all incoming patients.
            </small>
          )}
        </div>
        <div className="calendar-legend__item">
          <span
            className="calendar-legend__swatch"
            style={{ backgroundColor: MED_MANAGEMENT_COLOR, borderColor: '#8F2020' }}
            aria-hidden
          />
          <small className="fw-semibold calendar-legend__label">Med Management</small>
          {showDescriptions && (
            <small className="text-muted calendar-legend__description">
              Medication recommendations for patients with confirmed upcoming surgeries.
            </small>
          )}
        </div>
      </div>
    </div>
  );
}

function AppointmentsCalendar({
  allPatients = [],
  onSelectPatient,
  maskPatientNames = false,
  onMaskPatientNamesChange,
}) {
  const [excludeWeekends, setExcludeWeekends] = useState(true); // Default: 5-day work week
  const [currentView, setCurrentView] = useState('work_week');
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [isExpanded, setIsExpanded] = useState(false);

  const calendarComponents = useMemo(
    () => ({
      event: (props) => <AppointmentEvent {...props} maskPatientNames={maskPatientNames} />,
    }),
    [maskPatientNames],
  );

  const titleAccessor = useMemo(
    () => (event) => {
      const fullName = event.patientName || event.title || '';
      return maskPatientNames ? getPatientPrivacyLabel(fullName) : fullName;
    },
    [maskPatientNames],
  );

  /** Initial scroll position for time-grid views (day / week); time-of-day only. */
  const scrollToTime = useMemo(() => new Date(1970, 0, 1, 7, 0, 0), []);

  const calendarView = currentView === 'week' || currentView === 'work_week'
    ? (excludeWeekends ? 'work_week' : 'week')
    : currentView;

  const dateRange = useMemo(
    () => getRangeForView(currentDate, calendarView, excludeWeekends),
    [currentDate, calendarView, excludeWeekends]
  );

  const handleView = useCallback((view) => {
    if (view === 'week' || view === 'work_week') {
      setCurrentView((prev) => {
        const next = excludeWeekends ? 'work_week' : 'week';
        return prev === next ? prev : next;
      });
    } else {
      setCurrentView((prev) => (prev === view ? prev : view));
    }
  }, [excludeWeekends]);

  const handleNavigate = useCallback((nextDate) => {
    setCurrentDate((prev) => (sameCalendarDay(prev, nextDate) ? prev : nextDate));
  }, []);

  const handleRangeChange = useCallback((range) => {
    const nextDate = Array.isArray(range) && range.length > 0
      ? range[0]
      : range?.start;
    if (!nextDate) return;
    setCurrentDate((prev) => (sameCalendarDay(prev, nextDate) ? prev : nextDate));
  }, []);

  const events = useMemo(() => {
    return allPatients
      .filter(p => {
        const dateStr = patientSurgeryDate(p);
        return dateStr && p.name;
      })
      .map(patient => {
        const dateStr = (patientSurgeryDate(patient) || '').toString();
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return null;
        const endDate = new Date(d.getTime() + 60 * 60 * 1000);
        const isMedManagement = effectiveAppointmentType(patient) === 'SURGERY';
        return {
          id: listRowKey(patient),
          title: patient.name,
          patientName: patient.name,
          start: d,
          end: endDate,
          patientId: patient.id,
          appointmentId: patient.appointment_id,
          appointmentType: patient.appointment_type,
          isMedManagement,
        };
      })
      .filter(Boolean);
  }, [allPatients]);

  const handleSelectEvent = useCallback((event) => {
    if (event.patientId && onSelectPatient) {
      onSelectPatient(event.patientId, event.appointmentId);
    }
  }, [onSelectPatient]);

  const medManagementStats = useMemo(() => {
    const surgeries = allPatients.filter(p => {
      if (effectiveAppointmentType(p) !== 'SURGERY') return false;
      const dateStr = patientSurgeryDate(p);
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return isWithinInterval(d, { start: dateRange.start, end: dateRange.end });
    });
    const procedureCounts = {};
    surgeries.forEach(p => {
      const proc = p.surgery?.procedure;
      const procs = proc ? (Array.isArray(proc) ? proc : [proc]) : ['Unknown'];
      procs.forEach(pr => {
        const label = typeof pr === 'string' ? pr.replace(/_/g, ' ') : 'Unknown';
        procedureCounts[label] = (procedureCounts[label] || 0) + 1;
      });
    });
    const pieData = Object.entries(procedureCounts).map(([label, count]) => ({ label, count }));
    const requiringAction = surgeries.filter(p => p.has_medication_actions === true && p.approval_status === null).length;
    const approved = surgeries.filter(p => p.approval_status === 'approved').length;
    const modified = surgeries.filter(p => p.approval_status === 'modified').length;
    const denied = surgeries.filter(p => p.approval_status === 'denied').length;
    return { surgeries, pieData, requiringAction, approved, modified, denied };
  }, [allPatients, dateRange]);

  const jointDecisionsStats = useMemo(() => {
    const jdPatients = allPatients.filter(p => {
      if (effectiveAppointmentType(p) === 'SURGERY') return false;
      const dateStr = patientSurgeryDate(p);
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return isWithinInterval(d, { start: dateRange.start, end: dateRange.end });
    });
    return { count: jdPatients.length };
  }, [allPatients, dateRange]);

  useEffect(() => {
    if (!isExpanded) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsExpanded(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isExpanded]);

  const calendarHeight = isExpanded ? '100%' : undefined;

  const calendarPanel = (
    <div
      className={`appointments-calendar ${isExpanded ? 'calendar-expanded-panel' : 'calendar-normal-panel'}`}
    >
      <div
        className="calendar-grid-wrap"
        style={{
          height: calendarHeight,
          minHeight: isExpanded ? 0 : '12rem',
        }}
      >
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          dayLayoutAlgorithm="no-overlap"
          formats={CALENDAR_FORMATS}
          components={calendarComponents}
          titleAccessor={titleAccessor}
          eventPropGetter={eventStyleGetter}
          onSelectEvent={handleSelectEvent}
          views={excludeWeekends ? WEEKDAY_VIEWS : WEEKEND_VIEWS}
          view={calendarView}
          onView={handleView}
          date={currentDate}
          onNavigate={handleNavigate}
          onRangeChange={handleRangeChange}
          min={CALENDAR_MIN_TIME}
          max={CALENDAR_MAX_TIME}
          scrollToTime={scrollToTime}
          style={CALENDAR_STYLE}
          popup
        />
      </div>

      <CalendarLegend showDescriptions={!isExpanded} />

      <div className="calendar-controls-dock">
        <Form.Check
          type="switch"
          id={isExpanded ? 'exclude-weekends-expanded' : 'exclude-weekends'}
          label="Weekdays only (Mon–Fri)"
          checked={excludeWeekends}
          onChange={(e) => setExcludeWeekends(e.target.checked)}
          className="align-self-center mb-0"
        />
        <Button
          variant={maskPatientNames ? 'secondary' : 'outline-secondary'}
          size="sm"
          className="d-flex align-items-center justify-content-center px-2"
          onClick={() => onMaskPatientNamesChange?.(!maskPatientNames)}
          aria-label={maskPatientNames ? 'Show full patient names' : 'Mask patient names (initials only)'}
          aria-pressed={maskPatientNames}
          title={maskPatientNames ? 'Show patient names' : 'Mask patient names (privacy)'}
        >
          {maskPatientNames ? <Eye size={18} /> : <EyeSlash size={18} />}
        </Button>
        <Button
          variant="light"
          size="sm"
          className="border shadow-sm"
          onClick={() => setIsExpanded((v) => !v)}
          aria-label={isExpanded ? 'Exit expanded calendar' : 'Expand calendar'}
          title={isExpanded ? 'Exit expanded view' : 'Expand'}
        >
          {isExpanded ? <FullscreenExit size={18} /> : <ArrowsFullscreen size={18} />}
        </Button>
      </div>
    </div>
  );

  if (isExpanded) {
    return createPortal(calendarPanel, document.body);
  }

  return (
    <>
      {calendarPanel}
      <div className="calendar-analytics-wrap">
        <CalendarAnalyticsDashboard
          dateRange={dateRange}
          medManagementStats={medManagementStats}
          jointDecisionsStats={jointDecisionsStats}
          rangeLabelShort={getRangeLabel(calendarView)}
        />
      </div>
    </>
  );
}

export default React.memo(AppointmentsCalendar);
